import { useState } from "react";
import { Link } from "react-router-dom";
import { MailCheck, UserRound } from "lucide-react";
import { Field, FormError, PasswordField, SubmitButton } from "./Field";
import { useAccount } from "@/context/AccountContext";
import { errorMessage } from "@/lib/api";

export const LAST_CHECKOUT_KEY = "sfushop-last-checkout";

const lastCheckout = () => {
  try {
    return JSON.parse(sessionStorage.getItem(LAST_CHECKOUT_KEY) || "{}");
  } catch {
    return {};
  }
};

const ProfileLink = ({ testid, children }) => (
  <Link to="/account" data-testid={testid} className="inline-flex h-11 items-center rounded-full bg-acid px-5 font-mono text-[11px] uppercase tracking-[0.18em] text-ink transition-transform hover:-translate-y-0.5">
    {children}
  </Link>
);

export const CreateAccountCta = ({ testid = "order-account-cta" }) => {
  const { customer, register } = useAccount();
  const [form, setForm] = useState(() => ({ name: lastCheckout().name || "", email: lastCheckout().email || "", password: "" }));
  const [created, setCreated] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (customer === null) return null;

  if (customer) {
    return (
      <div data-testid={testid} className="w-full border border-line bg-ink p-5">
        {created ? (
          <p className="flex items-start gap-3 text-sm leading-relaxed text-bone/80" data-testid={`${testid}-created`}>
            <MailCheck className="mt-0.5 h-5 w-5 shrink-0 text-acid" /> Account creato! Conferma l&apos;email che ti ho appena inviato: l&apos;ordine comparirà nel tuo profilo con lo stato della spedizione.
          </p>
        ) : (
          <p className="text-sm leading-relaxed text-bone/80">Trovi questo ordine e lo stato della spedizione nel tuo profilo.</p>
        )}
        <div className="mt-4"><ProfileLink testid={`${testid}-profile-link`}>Vai al mio profilo</ProfileLink></div>
      </div>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    if (form.password.length < 8) {
      setError("La password deve avere almeno 8 caratteri.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      await register(form);
      setCreated(true);
    } catch (err) {
      setError(errorMessage(err, "Non sono riuscito a creare l'account. Controlla i dati."));
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} data-testid={testid} className="w-full space-y-4 border border-line bg-ink p-5">
      <p className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-acid">
        <UserRound className="h-4 w-4" /> Segui la tua spedizione
      </p>
      <p className="text-sm leading-relaxed text-bone/70">Crea un account in 10 secondi: ritrovi questo ordine, lo stato della spedizione e i tuoi dati per i prossimi drop.</p>
      {!lastCheckout().name && <Field label="Nome e cognome" required minLength={2} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} testid={`${testid}-name-input`} />}
      <Field label="Email dell'ordine" required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} testid={`${testid}-email-input`} />
      <PasswordField label="Scegli una password (min. 8)" required autoComplete="new-password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} testid={`${testid}-password-input`} />
      <FormError error={error} testid={`${testid}-error`} />
      <div className="flex flex-wrap items-center gap-4">
        <SubmitButton loading={loading} testid={`${testid}-submit-button`} loadingText="Creo l'account…">Crea account</SubmitButton>
        <Link to="/account/accedi" data-testid={`${testid}-login-link`} className="text-sm text-bone/70 underline underline-offset-4 hover:text-acid">Ho già un account</Link>
      </div>
    </form>
  );
};
